/**
 * The marking RULE for a test — how a question's marks are awarded.
 *
 * One control, shared by the admin test editor and the teacher's test form, so
 * the two cannot drift into offering different vocabularies for the same field.
 *
 * The rule is not the worth: each question is worth its own `marks`, and the
 * scheme only decides how those are awarded. See
 * docs/FeatureDesigns/AssessmentAndMarking/scoring-and-marking.md.
 *
 * NOT OFFERED HERE: `credit_rubric`. That one is not chosen, it follows — a test
 * with an uploaded mark-scheme document has it set automatically when the scheme
 * verifies (`extractMarkSchemeJob`, `attachMarkSchemeEndpoint`). Listing it as a
 * pickable option would invite a teacher to select credit marking on a paper
 * with no credit lines to mark against. When a set already carries it, the
 * control says so and keeps it rather than silently offering to overwrite it.
 */

import { MARKING_PRESETS, marksByType, presetKeyOf, type MarkingScheme } from '@padcanvas/shared';
import { IntegerInput, NumberInput } from './IntegerInput';
import './MarkingSelect.css';

/** The rule alone, with the preset name it was chosen under stripped off. Two
 *  schemes that mark identically compare equal here whatever they are called. */
function ruleOnly(scheme: MarkingScheme): string {
    const { preset: _name, ...rule } = scheme as MarkingScheme & { preset?: string };
    void _name;
    return JSON.stringify(rule);
}

/**
 * Match a stored scheme back to a preset key, or a sentinel.
 *
 * Exact when the scheme carries its `preset` tag, which every scheme chosen
 * through this control now does. The fallback is for schemes stored before the
 * tag existed, and it is LOSSY by nature: several presets are the same RULE
 * under different exam names — JEE Main and NEET are both `standard` at ¼, JEE
 * Main Section B and "no negative marking" are both `standard` at 0 — so it can
 * only return the first preset that marks the same way. Right to within the
 * name, which is all an untagged scheme can tell us.
 */
export function presetKeyForScheme(scheme?: MarkingScheme): string {
    if (!scheme) return 'none';
    if (scheme.kind === 'credit_rubric') return 'credit_rubric';
    if (scheme.kind === 'working_answer') return 'working_answer';
    // Chosen as "Custom" and given its own numbers: stays custom even when the
    // numbers happen to equal a preset's, or the fields would vanish mid-edit.
    if (scheme.preset === CUSTOM_TAG) return scheme.kind === 'multi_jee' ? 'custom_multi' : 'custom_single';
    const tagged = presetKeyOf(scheme);
    if (tagged) return tagged;
    for (const [k, p] of Object.entries(MARKING_PRESETS)) {
        // A by-type preset is its tag, not its fallback rule: an untagged
        // no-negative rule is not JEE Advanced.
        if (!p.byType && ruleOnly(p.scheme) === ruleOnly(scheme)) return k;
    }
    // A rule of a kind the custom fields cover — one read off a paper, say, at
    // +3 / −1 — is that custom rule, so it opens with its numbers.
    if (scheme.kind === 'standard') return 'custom_single';
    if (scheme.kind === 'multi_jee') return 'custom_multi';
    return 'custom';
}

/**
 * A rule no preset matches, named by its own numbers so it can be checked
 * against the paper at a glance — "+3 / −1 (custom)" rather than "Custom
 * (kept)". `worth` is what one question is worth, when the caller knows it
 * (a section read off the paper prints it); without it the penalty is said as
 * a share of the marks.
 */
export function customRuleLabel(scheme: MarkingScheme, worth?: number): string {
    const n = (v: number) => String(Math.round(v * 100) / 100);
    switch (scheme.kind) {
        case 'standard': {
            const f = scheme.negativeFraction ?? 0;
            if (worth) return `+${n(worth)} / ${f ? `−${n(f * worth)}` : '0'} (custom)`;
            return f ? `Wrong: −${Math.round(f * 100)}% of the marks (custom)` : 'No negative (custom)';
        }
        case 'multi_partial':
            return `Proportional to right options · wrong −${Math.round((scheme.negativeFraction ?? 0) * 100)}% (custom)`;
        case 'multi_jee':
            return `${worth ? `+${n(worth)} all · ` : ''}+${n(scheme.perOption ?? 1)} per option / −${n(scheme.negative ?? 2)} (custom)`;
        default:
            return 'Custom (kept)';
    }
}

/** What a newly chosen working + option rule starts at — the commonest school
 *  split (4 for working, ±1 on the option). The teacher edits the three numbers. */
const DEFAULT_WORKING_ANSWER: MarkingScheme = { kind: 'working_answer', workingMarks: 4, answerMarks: 1, wrongPenalty: 1 };

type WorkingAnswer = Extract<MarkingScheme, { kind: 'working_answer' }>;

/**
 * The three numbers of a paper's own "N for working, +A right, −P wrong" rule.
 * Whole marks only — the rule refuses anything else on the way in.
 * See docs/FeatureDesigns/AssessmentAndMarking/paper-stated-marking-target.md §3.
 */
export function WorkingAnswerFields({ value, onChange }: { value: WorkingAnswer; onChange: (s: WorkingAnswer) => void }) {
    const field = (key: 'workingMarks' | 'answerMarks' | 'wrongPenalty', label: string, prefix: string) => (
        <label className="working-answer-field">
            <span>{label}</span>
            <span className="working-answer-input">
                {prefix && <span aria-hidden="true">{prefix}</span>}
                <IntegerInput
                    min={0}
                    step={1}
                    inputMode="numeric"
                    value={value[key]}
                    onChange={n => { if (n != null) onChange({ ...value, [key]: n }); }}
                />
            </span>
        </label>
    );
    return (
        <span className="working-answer-fields">
            {field('workingMarks', 'Working', '')}
            {field('answerMarks', 'Right option', '+')}
            {field('wrongPenalty', 'Wrong option', '−')}
        </span>
    );
}

/** The presets that mark each question by its type (JEE Advanced). */
const BY_TYPE_PRESETS = Object.entries(MARKING_PRESETS).filter(([, p]) => p.byType);

/** The tag a rule given its own numbers carries. Not a preset key. */
const CUSTOM_TAG = 'custom';

type Standard = Extract<MarkingScheme, { kind: 'standard' }>;
type MultiJee = Extract<MarkingScheme, { kind: 'multi_jee' }>;

/** What "Custom" starts from: the rule already chosen, when it is the same kind. */
function startCustom(kind: 'single' | 'multi', from: MarkingScheme | undefined): MarkingScheme {
    if (kind === 'single') {
        return { kind: 'standard', negativeFraction: from?.kind === 'standard' ? from.negativeFraction ?? 0 : 0, preset: CUSTOM_TAG };
    }
    return from?.kind === 'multi_jee'
        ? { kind: 'multi_jee', negative: from.negative ?? 2, perOption: from.perOption ?? 1, preset: CUSTOM_TAG }
        : { kind: 'multi_jee', negative: 1, perOption: 1, preset: CUSTOM_TAG };
}

const round2 = (v: number) => Math.round(v * 100) / 100;

/**
 * A custom rule's numbers, as a paper prints them:
 *   one answer (MCQ, numerical) — max (= right), −wrong, blank;
 *   multiple correct — max (all right), +each right option, −any wrong, blank.
 *
 * MCQ / numerical keep the penalty as a share of the question's worth (that is
 * the `standard` rule), so −q is shown and edited against `worth` — the fields
 * need it. Max is the worth itself: editable only where the caller can store it
 * (`onWorthChange`, a section's marks per question on the paper's draft).
 * Blank is 0 under every rule the marker knows, so it is shown, not edited.
 */
export function CustomRuleFields({ value, onChange, worth, onWorthChange }: {
    value: Standard | MultiJee;
    onChange: (s: MarkingScheme) => void;
    worth: number;
    onWorthChange?: (n: number) => void;
}) {
    const num = (label: string, prefix: string, v: number, set: (n: number) => void, opts: { min?: number; disabled?: boolean; unknown?: boolean } = {}) => (
        <label className="working-answer-field">
            <span>{label}</span>
            <span className="working-answer-input">
                {prefix && <span aria-hidden="true">{prefix}</span>}
                <NumberInput
                    min={opts.min ?? 0}
                    step="any"
                    inputMode="decimal"
                    // allowEmpty, with null ignored: leaving an empty field puts
                    // back what it had, and an unknown worth stays unknown.
                    allowEmpty
                    value={opts.unknown ? null : round2(v)}
                    placeholder={opts.unknown ? '?' : undefined}
                    disabled={opts.disabled}
                    onChange={n => { if (n != null) set(n); }}
                />
            </span>
        </label>
    );
    const maxField = (label: string) => onWorthChange
        // Whole marks: a question's worth is stored as an integer.
        // A paper read without its marks: blank, for the teacher to fill in.
        ? num(label, '+', worth, n => { const w = Math.floor(n); if (w > 0) onWorthChange(w); }, { min: 0, unknown: !(worth > 0) })
        : num(label, '+', worth, () => {}, { disabled: true });
    // Not attempted: signed, so a paper's "−1 if left blank" can be said.
    // 0 is stored as no field at all — what every paper before this carried.
    const blank = num('Not attempted', '', value.blank ?? 0, b => {
        const { blank: _b, ...rest } = value;
        onChange({ ...rest, ...(b !== 0 ? { blank: b } : {}), preset: CUSTOM_TAG });
    }, { min: -100 });

    if (value.kind === 'standard') {
        const wrong = (value.negativeFraction ?? 0) * worth;
        return (
            <span className="working-answer-fields">
                {maxField('Full marks')}
                {num('Wrong', '−', wrong, q => onChange({ ...value, negativeFraction: worth > 0 ? q / worth : 0, preset: CUSTOM_TAG }),
                    // Stored as a share of the max, so it waits for one.
                    { disabled: !(worth > 0) })}
                {blank}
            </span>
        );
    }
    return (
        <span className="working-answer-fields">
            {maxField('Full marks')}
            {num('Partial: per right option', '+', value.perOption ?? 1, p => p > 0 && onChange({ ...value, perOption: p, preset: CUSTOM_TAG }))}
            {num('Wrong', '−', value.negative ?? 2, q => onChange({ ...value, negative: q, preset: CUSTOM_TAG }))}
            {blank}
        </span>
    );
}

export interface MarkingSelectProps {
    value: MarkingScheme | undefined;
    onChange: (s: MarkingScheme | undefined) => void;
    /** Sections only: undefined means "use the test's rule" rather than "none". */
    allowInherit?: boolean;
    /** Tests require a rule (TR-2). With this set, "no marking" is not offered
     *  and an unset value shows as an unchosen prompt, so saving can block on it
     *  instead of quietly producing a test that awards nothing. */
    required?: boolean;
    id?: string;
    className?: string;
    /** What one question is worth, when known — names a custom rule by its
     *  numbers ("+3 / −1"), and lets "Custom" offer its number fields. */
    worth?: number;
    /** Where a custom rule's max is stored, when the caller can store it. */
    onWorthChange?: (n: number) => void;
    /** Say the rule by its numbers, not by an exam's name: the dropdown picks
     *  only how a question is marked (one answer, multiple correct, rubric…)
     *  and the fields beside it carry the marks. Needs a worth to say them
     *  against (`worth` or `onWorthChange`); without one, the named presets. */
    byNumbers?: boolean;
    /** With `byNumbers`: what the section's questions take — one answer (MCQ,
     *  numerical) or multiple correct options. When the rule already fits it,
     *  there is nothing to pick and only the numbers are shown. */
    answerShape?: 'one' | 'multi';
}

export function MarkingSelect(props: MarkingSelectProps) {
    const { worth, onWorthChange, byNumbers } = props;
    if (byNumbers && ((worth ?? 0) > 0 || !!onWorthChange)) return <MarkingByNumbers {...props} />;
    return <MarkingByPreset {...props} />;
}

/**
 * The rule as its numbers. A preset's name ("JEE Main Section B") says less
 * than "+4 / −0" beside a section a teacher is checking against the paper, and
 * two names for one rule only confuse; so the dropdown picks the kind of
 * marking and the fields carry the marks. Any `standard` rule opens in the
 * one-answer fields, any `multi_jee` in the multiple-correct ones, whatever
 * preset it was chosen under.
 */
function MarkingByNumbers({ value, onChange, required, id, className, worth, onWorthChange, answerShape }: MarkingSelectProps) {
    const key = !value ? (required ? '' : 'none')
        // Marked by each question's type (JEE Advanced): no one rule to show numbers for.
        : marksByType(value) ? `by_type:${presetKeyOf(value)}`
        : value.kind === 'standard' ? 'one'
        : value.kind === 'multi_jee' ? 'multi'
        : value.kind === 'rubric' || value.kind === 'working_answer' || value.kind === 'credit_rubric' ? value.kind
        : 'other';
    const select = (
        <select
            id={id}
            className={className ?? 'test-marking-select'}
            value={key}
            required={required}
            onChange={e => {
                const k = e.target.value;
                if (k === 'none' || k === '') onChange(undefined);
                else if (k === 'one') onChange(startCustom('single', value));
                else if (k === 'multi') onChange(startCustom('multi', value));
                else if (k === 'rubric') onChange(MARKING_PRESETS.boards.scheme);
                else if (k === 'working_answer') onChange(value?.kind === 'working_answer' ? value : DEFAULT_WORKING_ANSWER);
                else if (k.startsWith('by_type:')) onChange(MARKING_PRESETS[k.slice('by_type:'.length)].scheme);
            }}
        >
            {required && key === '' && <option value="" disabled>Choose how it is marked…</option>}
            {!required && <option value="none">No marking</option>}
            <option value="one">One answer — option or number</option>
            <option value="multi">Multiple correct options</option>
            <option value="rubric">Partial credit (rubric, no negative)</option>
            <option value="working_answer">Working + option</option>
            {key === 'credit_rubric' && <option value="credit_rubric">From the uploaded mark scheme</option>}
            {/* An exam that marks each question type its own way — the one rule a
                section of mixed types can take without being split by type. */}
            {BY_TYPE_PRESETS.map(([k, p]) => <option key={k} value={`by_type:${k}`}>{p.label} — by question type</option>)}
            {key === 'other' && value && <option value="other">{customRuleLabel(value, worth)}</option>}
        </select>
    );
    const fields = key.startsWith('by_type:') ? null
        : value?.kind === 'standard' || value?.kind === 'multi_jee'
        ? <CustomRuleFields value={value} onChange={onChange} worth={worth ?? 0} onWorthChange={onWorthChange} />
        : value?.kind === 'working_answer'
            ? <WorkingAnswerFields value={value} onChange={onChange} />
            : null;
    if (!fields) return select;
    // The questions settle the kind and the rule fits it: the numbers alone.
    // A rule that does not fit them (multiple correct on single-answer
    // questions) keeps the dropdown, so the mismatch is in view and fixable.
    if (answerShape && key === answerShape) return fields;
    return <span className="marking-select-with-fields">{select}{fields}</span>;
}

function MarkingByPreset({ value, onChange, allowInherit, required, id, className, worth, onWorthChange }: MarkingSelectProps) {
    // No local memory of what was clicked: the chosen preset rides on the scheme
    // itself, so the selection survives the round trip through the parent and,
    // now, through storage. Before that tag existed the second of each
    // identically-marking pair could never appear chosen — picking "No negative
    // marking" snapped the control to "JEE Main Section B (numerical)".
    const key = value ? presetKeyForScheme(value) : (allowInherit ? 'inherit' : required ? '' : 'none');
    // The custom fields are said against a question's worth: offered where it is
    // known, or where the caller can take one (a draft read without its marks —
    // the teacher types the max in first).
    const canCustom = (worth ?? 0) > 0 || !!onWorthChange;

    const select = (
        <select
            id={id}
            className={className ?? 'test-marking-select'}
            value={key}
            required={required}
            onChange={e => {
                const k = e.target.value;
                if (k === 'inherit' || k === 'none' || k === '') onChange(undefined);
                else if (k === 'working_answer') onChange(value?.kind === 'working_answer' ? value : DEFAULT_WORKING_ANSWER);
                else if (k === 'custom_single') onChange(startCustom('single', value));
                else if (k === 'custom_multi') onChange(startCustom('multi', value));
                else if (k !== 'custom' && k !== 'credit_rubric') onChange(MARKING_PRESETS[k].scheme);
            }}
        >
            {required && key === '' && <option value="" disabled>Choose how this test is marked…</option>}
            {allowInherit && <option value="inherit">Use test default</option>}
            {!required && <option value="none">No marking</option>}
            {Object.entries(MARKING_PRESETS).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
            {/* A school paper's own split — marks for the working, ± on the option. */}
            <option value="working_answer">Working + option (marks for working, ± on the answer)</option>
            {/* Set by uploading a mark scheme, never picked. Shown only when already in force. */}
            {key === 'credit_rubric' && <option value="credit_rubric">From the uploaded mark scheme</option>}
            {/* The paper's own numbers, where the worth is known to put them against. */}
            {canCustom && <option value="custom_single">Custom — single answer or number (+right / −wrong)</option>}
            {canCustom && <option value="custom_multi">Custom — multiple correct (+per right option / −any wrong)</option>}
            {/* A custom rule where its numbers cannot be shown: named by them instead. */}
            {(key === 'custom' || (!canCustom && (key === 'custom_single' || key === 'custom_multi'))) && value
                && <option value={key}>{customRuleLabel(value, worth)}</option>}
        </select>
    );
    // A rule's numbers sit beside it whenever they can be said: a custom rule
    // to edit them, a preset to show what it gives here (JEE Main Section B at
    // 4 marks: +4 / −0). Changing a preset's penalty makes it custom; changing
    // the max keeps the preset — the worth is the question's, not the rule's.
    // Not for a by-type preset: its numbers differ per question type, and the
    // fields can only say one rule.
    if (canCustom && (value?.kind === 'standard' || value?.kind === 'multi_jee') && !marksByType(value)
        && (key === 'custom_single' || key === 'custom_multi' || !!MARKING_PRESETS[key])) {
        return (
            <span className="marking-select-with-fields">
                {select}
                <CustomRuleFields value={value} onChange={onChange} worth={worth ?? 0} onWorthChange={onWorthChange} />
            </span>
        );
    }
    if (value?.kind !== 'working_answer') return select;
    return (
        <span className="marking-select-with-fields">
            {select}
            <WorkingAnswerFields value={value} onChange={onChange} />
        </span>
    );
}
